import { Box, Chip, Stack, Typography } from '@mui/material';

import palette from 'src/theme/palette';

import { formatCompactCurrency } from './formatters';
import { ProductThumb } from './ProductThumb';
import type { RetentionFirstPurchaseLtvStat } from './types';

export interface FirstPurchaseLtvListProps {
  data: RetentionFirstPurchaseLtvStat[];
  maxRows: number;
  showFooter?: boolean;
  layout?: 'card' | 'modal';
}

export function FirstPurchaseLtvList({
  data,
  maxRows,
  showFooter = true,
  layout = 'card',
}: FirstPurchaseLtvListProps) {
  const rows = data.slice(0, maxRows);
  const maxLtv = Math.max(1, ...rows.map((row) => row.ltv));

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Stack
        spacing={layout === 'modal' ? 1.35 : 1.75}
        sx={{ minHeight: layout === 'card' ? 280 : undefined }}
      >
        {rows.map((item, index) => (
          <Box
            key={`${item.productId}-${index}`}
            sx={{
              width: '100%',
              minWidth: 0,
              p: 1.25,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'rgba(15, 23, 42, 0.06)',
              bgcolor: 'rgba(248, 250, 252, 0.65)',
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                width: '100%',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 22,
                  minWidth: 22,
                  minHeight: 40,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    color: palette.text.secondary,
                    fontWeight: 700,
                    lineHeight: 1,
                    fontSize: '0.95rem',
                  }}
                >
                  {index + 1}
                </Typography>
              </Box>

              <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                <ProductThumb
                  imageUrl={item.productImageUrl}
                  title={item.productTitle}
                  size={40}
                  radius={10}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.35,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 0.5,
                        columnGap: 0.75,
                      }}
                    >
                      <Typography
                        component="span"
                        className="retention-pdf-unclamp"
                        sx={{
                          color: palette.text.primary,
                          fontWeight: 700,
                          lineHeight: 1.25,
                          fontSize: '0.88rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.productTitle}
                      </Typography>
                      {item.lowSample ? (
                        <Chip
                          label="Limited data"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: 'rgba(245, 158, 11, 0.12)',
                            color: '#b45309',
                            border: '1px solid rgba(245, 158, 11, 0.35)',
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                    </Box>
                  </Box>

                  <Typography
                    component="span"
                    sx={{
                      color: palette.text.primary,
                      fontWeight: 800,
                      fontSize: '0.95rem',
                      flexShrink: 0,
                      lineHeight: 1.2,
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatCompactCurrency(item.ltv)}
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  className="retention-pdf-caption-wrap"
                  sx={{
                    color: palette.text.secondary,
                    display: 'block',
                    lineHeight: 1.35,
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    wordBreak: 'break-word',
                  }}
                >
                  {item.firstPurchaseCustomers} first buyers ·{' '}
                  {item.repeatCustomers} came back ·{' '}
                  {item.avgOrdersPerCustomer.toFixed(1)} avg orders/customer ·{' '}
                  {formatCompactCurrency(item.grossMarginLtv)} margin LTV
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 1.25,
                height: 10,
                borderRadius: 99,
                bgcolor: '#eef2f7',
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  width: `${Math.max(6, Math.round((item.ltv / maxLtv) * 100))}%`,
                  height: '100%',
                  borderRadius: 99,
                  background: item.lowSample
                    ? 'linear-gradient(90deg, rgba(148,163,184,0.75) 0%, rgba(100,116,139,0.95) 100%)'
                    : 'linear-gradient(90deg, rgba(16,185,129,0.72) 0%, rgba(5,150,105,1) 100%)',
                  boxShadow: item.lowSample
                    ? 'none'
                    : '0 8px 18px -12px rgba(5, 150, 105, 0.8)',
                }}
              />
            </Box>
          </Box>
        ))}

        {showFooter ? (
          <Typography
            variant="caption"
            sx={{
              color: palette.text.secondary,
              pt: 0.5,
              lineHeight: 1.45,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            Ranked by starter-product LTV with a light audience-size weighting,
            so tiny cohorts do not dominate the list. Bar length is relative to
            the strongest row shown here.
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

interface FirstPurchaseLtvChartProps {
  data: RetentionFirstPurchaseLtvStat[];
}

export function FirstPurchaseLtvChart({ data }: FirstPurchaseLtvChartProps) {
  return <FirstPurchaseLtvList data={data} maxRows={5} layout="card" />;
}

export default FirstPurchaseLtvChart;
